import { computed, type ComputedRef } from 'vue'
import type { TypeOption } from '@siclop/kscommercial-vue'
import { useActivityTypeStore } from '@/modules/activity/stores'
import { useServiceTypeStore } from '@/modules/service-type/stores'
import { useEstimationScaleStore } from '@/modules/estimation/stores'

export interface UseDocumentLineTypeOptionsReturn {
  activityTypeOptions: ComputedRef<TypeOption[]>
  serviceTypeOptions: ComputedRef<TypeOption[]>
  /** Charge les types d'activité, de prestation et les abaques s'ils ne le sont pas déjà. */
  ensureLoaded: () => Promise<void>
}

/**
 * Options de type de ligne de devis / facture (types d'activité et de prestation de KSProject),
 * au format attendu par l'éditeur de lignes de kscommercial-vue.
 *
 * Tarif proposé (`defaultRate`) :
 * - type de prestation : son `defaultHourlyRate` ;
 * - type d'activité : le `defaultHourlyRate` de son abaque d'estimation (`estimationScaleId`).
 *   Pas d'abaque, abaque absent du store ou tarif à 0 : aucun prix proposé.
 */
export function useDocumentLineTypeOptions(): UseDocumentLineTypeOptionsReturn {
  const activityTypeStore = useActivityTypeStore()
  const serviceTypeStore = useServiceTypeStore()
  const estimationScaleStore = useEstimationScaleStore()

  const scaleRates = computed(() => {
    const rates = new Map<number, number>()
    for (const scale of estimationScaleStore.datas) {
      if (scale.defaultHourlyRate) rates.set(scale.id, scale.defaultHourlyRate)
    }
    return rates
  })

  const activityTypeOptions = computed<TypeOption[]>(() =>
    activityTypeStore.datas.map(at => ({
      value: at.id,
      label: at.label,
      defaultRate: at.estimationScaleId != null ? scaleRates.value.get(at.estimationScaleId) : undefined
    }))
  )

  const serviceTypeOptions = computed<TypeOption[]>(() =>
    serviceTypeStore.datas.map(st => ({ value: st.id, label: st.label, defaultRate: st.defaultHourlyRate || undefined }))
  )

  async function ensureLoaded(): Promise<void> {
    // Référentiels chargés au démarrage (bootstrap) : aucun appel s'ils le sont déjà.
    await Promise.all([
      activityTypeStore.ensureLoaded(),
      serviceTypeStore.ensureLoaded(),
      estimationScaleStore.ensureLoaded()
    ])
  }

  return { activityTypeOptions, serviceTypeOptions, ensureLoaded }
}
